import { IPermission, IUser } from "../services";

// Single source of truth for permission-tier checks across the portal.
// Replaces the ad-hoc inline `(user?.permissions?.permission_id as IPermission).permission === ...`
// checks that were previously copy-pasted across pages/components.

// #region permissionOf
/** Safely reads a user's permission level, or undefined if none is set. */
export const permissionOf = (user?: IUser | null): IPermission["permission"] | undefined =>
    (user?.permissions?.permission_id as IPermission | undefined)?.permission;

// #region isHighAccess
/** High access: technicians plus both super tiers. */
export const isHighAccess = (user?: IUser | null): boolean => {
    const perm = permissionOf(user);
    return perm === "TECHNICIAN" || perm === "SUPERADMIN" || perm === "SUPERUSER";
};

// #region canSeePastMaintenance
/** Users flagged in `order.user.can_see_past_maintenance` bypass the maintenance overlay. */
export const canSeePastMaintenance = (user?: IUser | null): boolean =>
    user?.can_see_past_maintenance === true;

// #region isSuperAccess
/** Super access: the two super tiers only. */
export const isSuperAccess = (user?: IUser | null): boolean => {
    const perm = permissionOf(user);
    return perm === "SUPERADMIN" || perm === "SUPERUSER";
};

// #region isSuperAdmin
/**
 * SUPERADMIN only — strictly narrower than `isSuperAccess`, which also admits SUPERUSER.
 * Use for actions that move real money (e.g. refunding an invoice). Note that the portal
 * gate is cosmetic: the server must enforce the same check independently.
 */
export const isSuperAdmin = (user?: IUser | null): boolean => permissionOf(user) === "SUPERADMIN";
